import { Button, Form, Modal, Checkbox, Row, Col, Typography, Divider } from "antd";
import Draggable from "react-draggable";
import { formItemLayout } from "../../../constants/ApplicationConstants/FormConstants";
import { useReactDraggable } from "../../../hooks/useReactDraggable";
import styles from "../../MasterManagement/index.module.less";
import { usePaymentDetails } from "./index.hook";
import Loading from "../../common/Loading";
import moment from "moment-timezone";
import type { FormattedABRRequest, SummaryDetailsInterface } from "../../../shared/interface/advanceBookingRequest";

const { Text } = Typography;

interface PaymentSummaryProps {
  data: FormattedABRRequest | null;
  handleRefreshList: () => void;
  isVisible: boolean;
  handleOnClose: () => void;
  loading: boolean;
  summaryData: SummaryDetailsInterface | null;
}

// Utility render for label-value rows
const RenderLabelValue = ({ label, value, danger = false, span = 12 }: { label: string; value: string | number | null; danger?: boolean; span?: number }) => (
  <Col span={span}>
    <Text strong>{label}</Text>
    <br />
    <Text type={danger ? "danger" : undefined}>{danger && value ? `${String(value).split(":")[0]} hr ${String(value).split(":")[1]} min` : value || ""}</Text>
  </Col>
);

const PaymentDetailsModal = ({ data, handleRefreshList, isVisible, handleOnClose, loading, summaryData }: PaymentSummaryProps) => {
  const { bounds, onStart } = useReactDraggable();

  const config = usePaymentDetails({
    data,
    handleRefreshList,
    isVisible,
    handleOnClose,
    loading,
    summaryData
  });

  const formatDate = (date: string | null) => (date ? moment(date).format("DD-MM-YYYY") : "");
  const formatTime = (time: string | null) => (time ? moment(time).format("HH:mm") : "");
  const formatUTCTime = (time: string | null) => (time ? moment.utc(time).format("HH:mm") : "");

  return (
    <Modal
      title={<div className="modal-container">Payment Summary</div>}
      maskClosable={false}
      open={isVisible}
      onCancel={handleOnClose}
      centered
      width="40%"
      footer={null}
      modalRender={(modal) => (
        <Draggable bounds={bounds} handle=".ant-modal-header" onStart={(event, uiData) => onStart(event as any, uiData)}>
          {modal}
        </Draggable>
      )}
    >
      {loading || !summaryData ? (
        <div className={styles["textCenter"]}>
          <Loading />
        </div>
      ) : (
        <Form {...formItemLayout} onFinish={config?.handleSubmit} className={`gx-pl-4 gx-pr-4 ${styles["formDiv"]}`}>
          {/* Header */}
          <Row justify="space-between" align="middle">
            <Col>
              <Text type="secondary">Date: </Text>
              <Text strong>{formatDate(summaryData?.booking_in_time)}</Text>
            </Col>
          </Row>

          <Divider />

          {/* Booking Info */}
          <Row gutter={[16, 12]}>
            <RenderLabelValue label="Booking ID:" value={summaryData?.booking_id} />
            <RenderLabelValue label="Overdue Time:" value={summaryData?.extra_time} danger />
            <RenderLabelValue label="Booking In-Time:" value={formatTime(summaryData?.booking_in_time)} />
            <RenderLabelValue label="Parking In-Time:" value={formatUTCTime(summaryData?.actual_storage_time)} />
            <RenderLabelValue label="Booking Out-Time:" value={formatTime(summaryData?.booking_out_time)} />
            <RenderLabelValue label="Parking Out-Time:" value={formatUTCTime(summaryData?.actual_retrieval_time)} />
          </Row>

          <Divider />

          {/* Charges */}
          {(summaryData?.amount_to_be_paid?.parking_amount ? summaryData?.amount_to_be_paid?.parking_amount : 0) > 0 && (
            <Row style={{ marginBottom: 12 }}>
              <Col span={12}>
                <Text strong>Parking Amount:</Text>
              </Col>
              <Col span={12} style={{ textAlign: "right" }}>
                <Text>₹ {summaryData.amount_to_be_paid.parking_amount}</Text>
              </Col>
            </Row>
          )}

          {(summaryData?.amount_to_be_paid?.valet_parking_amount ? summaryData?.amount_to_be_paid?.valet_parking_amount : 0) > 0 && (
            <Row style={{ marginBottom: 12 }}>
              <Col span={12}>
                <Text strong>Valet Parking Amount:</Text>
              </Col>
              <Col span={12} style={{ textAlign: "right" }}>
                <Text>₹ {summaryData.amount_to_be_paid.valet_parking_amount}</Text>
              </Col>
            </Row>
          )}

          <Row style={{ marginBottom: 12 }}>
            <Col span={12}>
              <Text strong>Overdue Amount:</Text>
            </Col>
            <Col span={12} style={{ textAlign: "right" }}>
              <Text>₹ {summaryData.amount_to_be_paid.total_overdue_amount}</Text>
            </Col>
          </Row>

          <Row style={{ marginBottom: 12 }}>
            <Col span={12}>
              <Checkbox name="penalty_amount" checked={config.isPenaltyChecked} onChange={config.handleCheckboxChange}>
                <Text strong>Penalty Amount:{` ( ${summaryData.charge.penalty_amount ?? 0} )`}</Text>
              </Checkbox>
            </Col>
            <Col span={12} style={{ textAlign: "right" }}>
              <Text>₹ {config.isPenaltyChecked ? summaryData.charge.penalty_amount ?? 0 : 0}</Text>
            </Col>
          </Row>

          {!summaryData?.is_valet_parking && (
            <Row style={{ marginBottom: 12 }}>
              <Col span={12}>
                <Checkbox name="valet_parking_amount" checked={config.isValetParkingChecked} onChange={config.handleCheckboxChange}>
                  <Text strong>Valet Parking Amount:{` ( ${summaryData.charge.valet_parking_amount ?? 0} )`}</Text>
                </Checkbox>
              </Col>
              <Col span={12} style={{ textAlign: "right" }}>
                <Text>₹ {config.isValetParkingChecked ? summaryData.charge.valet_parking_amount ?? 0 : 0}</Text>
              </Col>
            </Row>
          )}

          <Divider />

          {/* Total */}
          <Row style={{ marginBottom: 24 }}>
            <Col span={12}>
              <Text strong style={{ fontSize: 16 }}>
                Total Extra Amount:
              </Text>
            </Col>
            <Col span={12} style={{ textAlign: "right" }}>
              <Text strong style={{ fontSize: 16 }}>
                ₹ {config.totalAmount ?? 0}
              </Text>
            </Col>
          </Row>

          {/* Footer Buttons */}
          <Form.Item className={styles["form-itemFloatR20"]}>
            <Button htmlType="submit" className={`gx-mb-0 button-gradiant ${styles["button-m01"]}`}>
              Submit
            </Button>
          </Form.Item>
          <Form.Item className={styles["form-itemFloatR20"]}>
            <Button onClick={handleOnClose} className={`gx-mb-0 button-gradiant ${styles["button-m01"]}`}>
              Close
            </Button>
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
};

export default PaymentDetailsModal;
